import { useCallback, useEffect, useRef } from "react";
import styled from "@emotion/styled";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import {
  executionBriefsLoadingSelector,
  selectExecutionBriefsOwners,
  selectExecutionBriefsResultCount,
  selectExecutionBriefsResults,
} from "../../redux/selectors/executionBriefs";
import {
  getExecutionBriefFilters,
  getExecutionBriefList,
  resetExecutionBrief,
} from "../../redux/actions/executionBriefListActions";
import { BriefType } from "../../constants/types";
import BriefList from "../../features/brief-list/components/brief-list/brief-list.component";
import { TExecutionBriefResults } from "../../redux/reducers/executionBriefsReducer";
import { RESULTS_PER_PAGE } from "../../utils/constants";
import { useURLQueryString } from "../../hooks/useUrlQueryString";
import debounce from "../../utils/debounce";
import _ from "lodash";
import { ExecutionBriefProps } from "../../features/brief-list/types/props/execution-brief.props";
import { getTotalPages } from "../../components/Pagination/pagination.functions";
import { selectRegionResults } from "../../redux/selectors/commonSelectors";
import { ExecutionBriefTemplateLabels } from "../../types/templates/execution-brief/execution-brief-template-labels.constant";

const Wrapper = styled.div`
  width: 100%;
  text-align: left;
  padding: 20px;
`;

export const ExecutionBriefList = () => {
  const dispatch = useDispatch();
  const {
    values: { limit = [RESULTS_PER_PAGE], page = [1], ...parameters },
  } = useURLQueryString();
  const limitRef = useRef(limit);
  const pageRef = useRef(page);
  const paramsRef = useRef(parameters ?? {});
  const owners = useSelector(selectExecutionBriefsOwners) ?? [];
  const regions = useSelector(selectRegionResults) ?? [];

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedFetch = useCallback(
    debounce((params) => dispatch(getExecutionBriefList(params)), 1000),
    []
  );

  useEffect(() => {
    if (!_.isEqual(paramsRef.current, parameters)) {
      if (_.isEqual(paramsRef.current, {}) || _.isEqual(parameters, {})) {
        dispatch(getExecutionBriefList(parameters));
      } else {
        debouncedFetch(parameters);
      }
      paramsRef.current = parameters;
    } else if (!_.isEqual([limitRef.current, pageRef.current], [limit, page])) {
      limitRef.current = limit;
      pageRef.current = page;
      dispatch(getExecutionBriefList({ limit, page, ...paramsRef.current }, true));
    }
  }, [debouncedFetch, dispatch, limit, page, parameters]);

  useEffect(() => {
    // get all execution briefs
    dispatch(getExecutionBriefFilters());
    dispatch(getExecutionBriefList({ limit, page, ...parameters }));

    return () => {
      dispatch(resetExecutionBrief());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const executionBriefList = useSelector(selectExecutionBriefsResults);
  const resultCount = useSelector(selectExecutionBriefsResultCount);
  const loading = useSelector(executionBriefsLoadingSelector);

  const convertData = (origData?: TExecutionBriefResults[]): ExecutionBriefProps[] => {
    if (origData) {
      return origData.map((item) => ({
        ...item,
        ownerName: `${item.briefOwner.firstName} ${item.briefOwner.lastName}`,
        date: moment(item.createdAt).format("MMM DD"),
        createdDate: moment(item.createdAt).format("YYYY-MM-DD"),
      }));
    } else {
      return [];
    }
  };

  const dataList = convertData(executionBriefList);
  const sortedDataList = dataList.sort((a, b) => (a.createdAt > b.createdAt ? -1 : 1)); // Sorted by created datetime descending

  return (
    <Wrapper>
      {sortedDataList && (
        <BriefList
          dataList={sortedDataList}
          briefType={BriefType.EXECUTION_BRIEF}
          filterOptionLists={{ users: owners, regions, templates: ExecutionBriefTemplateLabels }}
          loading={loading}
          pageCount={getTotalPages(resultCount)}
        />
      )}
    </Wrapper>
  );
};
